import React, { useState, useEffect, useRef } from "react"
import isEqual from "lodash/isEqual"
import {
  VStack,
  HStack,
  Text,
  Box,
  Grid,
  Select as ChakraSelect,
  Divider,
} from "@chakra-ui/react"
import Modal from "components/dialogs/Modal"
import Button from "components/Buttons/Button"
import NumberInput from "components/elements/NumberInput"
import { SegmentedControl } from "components/SegmentedControl"
import {
  SheetLayout, calculateSheetGrid, ExportFormat,
  AVERY_5392_SHEET,
} from "components/BadgeBuilder/utils/sheetPresets"
import {
  BadgeDisplayUnit,
  convertMmToUnit,
  convertUnitToMm,
} from "components/BadgeBuilder/utils/units"
import { BadgeTemplate } from "sharedTypes/badge"
import { DioboxEvent } from "sharedTypes"
import { GuestData } from "components/BadgeBuilder/utils/badgeDataResolver"
import SheetPreviewDiagram from "./SheetPreviewDiagram"

type Props = {
  isOpen: boolean
  onClose: () => void
  onSave: (sheet: SheetLayout) => void
  initialValues?: SheetLayout
  selectedBadge?: BadgeTemplate | null
  previewGuests?: GuestData[]
  event?: DioboxEvent
  selectedCount?: number
}

const DEFAULT_SHEET: SheetLayout = AVERY_5392_SHEET

type PagePreset = {
  label: string
  widthMm: number
  heightMm: number
}

const PAGE_PRESETS: PagePreset[] = [
  { label: "US Letter (8.5\" × 11\")", widthMm: 215.9, heightMm: 279.4 },
  { label: "US Legal (8.5\" × 14\")", widthMm: 215.9, heightMm: 355.6 },
  { label: "A4 (210 × 297 mm)", widthMm: 210, heightMm: 297 },
]

const UNIT_OPTIONS: { label: string; value: BadgeDisplayUnit }[] = [
  { label: "mm", value: "mm" },
  { label: "Inches", value: "in" },
]

type CompactFieldProps = {
  label: string
  valueMm: number
  onChange: (valueStr: string) => void
  min: number
  max: number
  unit: BadgeDisplayUnit
}

// Snap stepper values to step boundaries. Chakra's NumberInput doesn't do this
// natively — when stepping from a non-aligned value (e.g. 279.4 + 1 = 280.4),
// we round toward the direction of change so it lands on a clean step boundary.
const snapToStep = (value: number, displayValue: number, step: number): number => {
  const nearestStep = Math.round(value / step) * step
  if (Math.abs(value - nearestStep) < 0.001) return value
  if (value > displayValue) return Math.ceil(displayValue / step) * step

  return Math.floor(displayValue / step) * step
}

const CompactField = ({
  label, valueMm, onChange, min, max, unit,
}: CompactFieldProps) => {
  const displayValue = Math.round(convertMmToUnit(valueMm, unit) * 1000) / 1000
  const precision = unit === "in" ? 2 : 1
  const step = unit === "in" ? 0.01 : 1

  const handleChange = (valueStr: string) => {
    const value = Number(valueStr)
    if (Number.isNaN(value) || value < 0) return

    const snapped = snapToStep(value, displayValue, step)
    const mm = Math.round(convertUnitToMm(snapped, unit) * 100) / 100
    onChange(mm.toString())
  }

  return (
    <NumberInput
      label={label}
      value={displayValue}
      onChange={handleChange}
      min={convertMmToUnit(min, unit)}
      max={convertMmToUnit(max, unit)}
      step={step}
      precision={precision}
    />
  )
}

const CustomSheetModal = ({
  isOpen, onClose, onSave, initialValues,
  selectedBadge = null, previewGuests = [], event, selectedCount = 0,
}: Props) => {
  const [sheet, setSheet] = useState<SheetLayout>(DEFAULT_SHEET)
  const [unit, setUnit] = useState<BadgeDisplayUnit>("in")

  const updateField = (field: keyof SheetLayout) => (valueStr: string) => {
    const value = Number(valueStr)
    if (!Number.isNaN(value) && value >= 0) {
      setSheet((prev) => ({ ...prev, [field]: Math.round(value * 100) / 100 }))
    }
  }

  // Reset state on open. Intentionally omit `initialValues` so that the sheet
  // only resets at the moment of opening, not on every external prop change.
  // If saved sheet has non-preset page dimensions, snap to US Letter.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (isOpen) {
      const saved = initialValues || DEFAULT_SHEET
      const matchesPreset = PAGE_PRESETS.some(
        (p) => Math.abs(p.widthMm - saved.pageWidthMm) < 0.1
          && Math.abs(p.heightMm - saved.pageHeightMm) < 0.1,
      )
      if (!matchesPreset) {
        setSheet({
          ...saved,
          pageWidthMm: PAGE_PRESETS[0].widthMm,
          pageHeightMm: PAGE_PRESETS[0].heightMm,
        })
      } else {
        setSheet(saved)
      }
    }
  }, [isOpen])

  const pagePresetIndex = PAGE_PRESETS.findIndex(
    (p) => Math.abs(p.widthMm - sheet.pageWidthMm) < 0.1
      && Math.abs(p.heightMm - sheet.pageHeightMm) < 0.1,
  )

  const grid = calculateSheetGrid(sheet)
  const isValid = grid.columns >= 1 && grid.rows >= 1

  const isFirstTime = !initialValues
  const hasChanges = isFirstTime || !isEqual(sheet, initialValues)

  // Transient grid info overlay: appears when grid dimensions change, fades out
  const [gridInfoVisible, setGridInfoVisible] = useState(false)
  const gridTimerRef = useRef<ReturnType<typeof setTimeout>>()
  const isInitialMount = useRef(true)

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false

      return undefined
    }
    setGridInfoVisible(true)
    clearTimeout(gridTimerRef.current)
    gridTimerRef.current = setTimeout(() => setGridInfoVisible(false), 1500)

    return () => clearTimeout(gridTimerRef.current)
  }, [grid.columns, grid.rows])

  useEffect(() => {
    if (isOpen) isInitialMount.current = true
  }, [isOpen])

  const handlePagePresetChange = (value: string) => {
    const preset = PAGE_PRESETS[Number(value)]
    if (preset) {
      setSheet((prev) => ({
        ...prev,
        pageWidthMm: preset.widthMm,
        pageHeightMm: preset.heightMm,
      }))
    }
  }

  const handleSave = () => {
    onSave(sheet)
  }

  const actions = [
    <Button variant="outline" colorScheme="button.light" onClick={onClose}>
      Back
    </Button>,
    <Button onClick={handleSave} isDisabled={!isValid || !hasChanges}>
      Save
    </Button>,
  ]

  return (
    <Modal
      show={isOpen}
      hide={onClose}
      title="Custom Sheet Layout"
      size="3xl"
      actions={actions}
    >
      <Grid templateColumns="1fr auto" gap={8}>
        {/* Left: live preview */}
        <VStack spacing={4} align="stretch">
          {/* Unit toggle */}
          <Box
            bg="gray.50"
            borderRadius="md"
            borderWidth={1}
            borderColor="gray.200"
            p={3}
            display="flex"
            alignItems="center"
            justifyContent="center"
            position="relative"
          >
            <SheetPreviewDiagram
              format={ExportFormat.CUSTOM_SHEET}
              sheet={isValid ? sheet : null}
              columns={isValid ? grid.columns : null}
              rows={isValid ? grid.rows : null}
              selectedBadge={selectedBadge}
              previewGuests={previewGuests}
              event={event}
              selectedCount={isValid
                ? Math.min(selectedCount || grid.columns * grid.rows, grid.columns * grid.rows)
                : 0}
              diagramWidth={350}
              diagramHeight={450}
            />
            {isValid && (
              <Box
                position="absolute"
                top={2}
                left={0}
                right={0}
                textAlign="center"
                zIndex={2}
                pointerEvents="none"
                opacity={gridInfoVisible ? 1 : 0}
                transition="opacity 0.5s ease"
              >
                <Text
                  as="span"
                  bg="gray.600"
                  color="white"
                  fontSize="2xs"
                  sx={{ fontVariantNumeric: "tabular-nums" }}
                  fontWeight="medium"
                  px={2}
                  py={0.5}
                  borderRadius="full"
                >
                  {grid.columns} × {grid.rows}
                </Text>
              </Box>
            )}
          </Box>
          <HStack justify="space-between" align="center">
            <Text fontSize="xs" fontWeight="medium" color="gray.600">Units</Text>
            <SegmentedControl
              options={UNIT_OPTIONS}
              value={unit}
              onChange={(v) => setUnit(v as BadgeDisplayUnit)}
              size="sm"
            />
          </HStack>
        </VStack>

        {/* Right: form fields */}
        <VStack spacing={4} align="stretch" w="240px">
          {/* Page Size */}
          <VStack align="stretch" spacing={2}>
            <Box>
              <Text fontSize="sm" fontWeight="semibold">Page Size</Text>
              <Text fontSize="xs" color="gray.600">Physical dimensions of the paper</Text>
            </Box>
            <ChakraSelect
              size="sm"
              value={pagePresetIndex.toString()}
              onChange={(e) => handlePagePresetChange(e.target.value)}
            >
              {PAGE_PRESETS.map((preset, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <option key={i} value={i.toString()}>
                  {preset.label}
                </option>
              ))}
            </ChakraSelect>
          </VStack>

          <Divider />

          {/* Label Size */}
          <VStack align="stretch" spacing={2}>
            <Box>
              <Text fontSize="sm" fontWeight="semibold">Label Size</Text>
              <Text fontSize="xs" color="gray.600">Single label on the sheet</Text>
            </Box>
            <HStack spacing={2}>
              <CompactField label="W" valueMm={sheet.labelWidthMm} onChange={updateField("labelWidthMm")} min={5} max={1000} unit={unit} />
              <CompactField label="H" valueMm={sheet.labelHeightMm} onChange={updateField("labelHeightMm")} min={5} max={1000} unit={unit} />
            </HStack>
          </VStack>

          <Divider />

          {/* Margins */}
          <VStack align="stretch" spacing={2}>
            <Box>
              <Text fontSize="sm" fontWeight="semibold">Margins</Text>
              <Text fontSize="xs" color="gray.600">Page edge to first label</Text>
            </Box>
            <HStack spacing={2}>
              <CompactField label="T/B" valueMm={sheet.marginTopBottomMm} onChange={updateField("marginTopBottomMm")} min={0} max={500} unit={unit} />
              <CompactField label="L/R" valueMm={sheet.marginLeftRightMm} onChange={updateField("marginLeftRightMm")} min={0} max={500} unit={unit} />
            </HStack>
          </VStack>

          <Divider />

          {/* Gap (pitch) */}
          <VStack align="stretch" spacing={2}>
            <Box>
              <Text fontSize="sm" fontWeight="semibold">Gap</Text>
              <Text fontSize="xs" color="gray.600">Space between labels</Text>
            </Box>
            <HStack spacing={2}>
              <CompactField label="Col" valueMm={sheet.columnGapMm} onChange={updateField("columnGapMm")} min={0} max={500} unit={unit} />
              <CompactField label="Row" valueMm={sheet.rowGapMm} onChange={updateField("rowGapMm")} min={0} max={500} unit={unit} />
            </HStack>
          </VStack>
        </VStack>
      </Grid>
    </Modal>
  )
}

export default CustomSheetModal
